import { Injectable } from '@angular/core';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  GlobalThemeConfiguration,
  generateGlobalThemesCssStringsFromThemeConfig,
  globalThemeConstant,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, finalize, tap } from 'rxjs';
import {
  AddNewGlobalThemeConfig,
  GetProjectThemes,
  ResetThemesConfig,
  SetActiveTheme,
  UpdateActiveThemeConfig,
  UpdateGlobalThemeState,
  UpdateProjectThemes,
} from '../actions/global-theme.action';
import {
  AddProgressBarWorkflowStack,
  RemoveProgressBarWorkflowStack,
} from '../actions/progress-bar-workflow.action';
import { ApiService } from '../services/api-service/api.service';
import { ThemeUtilityService } from '../services/global-theme-utility/theme-utility.service';

export interface GlobalThemeStateModel {
  themes: GlobalThemeConfiguration[];
  selectedThemeIndex: number;
  defaultThemeIndex?: number;
}

@State<GlobalThemeStateModel>({
  name: 'globalThemeState',
  defaults: {
    themes: [],
    selectedThemeIndex: 0,
    defaultThemeIndex: 0,
  },
  children: [],
})
@Injectable()
export class GlobalThemeState {
  constructor(
    private readonly apiService: ApiService,
    private readonly alertHandlerService: AlertHandlerService,
    private readonly themeUtilityService: ThemeUtilityService,
    private readonly store: Store,
  ) {}

  @Selector()
  static getGlobalThemeState(state: GlobalThemeStateModel) {
    return state;
  }

  @Selector()
  static getActiveGlobalTheme(state: GlobalThemeStateModel) {
    const selectedTheme = state.themes[state.selectedThemeIndex];
    // Merge with the default theme so no properties are missed if the remote theme is out of date
    return selectedTheme;
    // return deepMerge(selectedTheme, globalThemeConstant);
  }

  @Action(GetProjectThemes)
  fetchProjectThemes(ctx: StateContext<GlobalThemeStateModel>, action: GetProjectThemes) {
    this.apiService
      .fetchProjectThemes(action.projectId)
      .pipe(
        tap((res) => {
          if (res?.length) {
            const activeThemeIndex = res.findIndex((v) => !!v.isDefault) ?? 0;
            const mappedThemes =
              res.map((v) =>
                // merge with default constant to add any missing properties in the theme config
                this.themeUtilityService.deepMergeWithGlobalThemeConstant(v.themeConfig, globalThemeConstant),
              ) ?? [];
            ctx.setState({
              ...ctx.getState(),
              themes: mappedThemes,
              selectedThemeIndex: activeThemeIndex,
            });
            this.themeUtilityService.generateCssFromActiveTheme(mappedThemes[activeThemeIndex]);
          } else {
            const themes = [globalThemeConstant];
            ctx.setState({
              ...ctx.getState(),
              themes: themes,
              selectedThemeIndex: 0,
            });
            this.themeUtilityService.generateCssFromActiveTheme(themes[0]);
          }
        }),
        catchError((err) => {
          this.alertHandlerService.showAlertFn('error', 'Unable to fetch project themes configuration');
          throw new Error(err);
        }),
      )
      .subscribe();
  }

  @Action(UpdateProjectThemes)
  updateProjectThemes(ctx: StateContext<GlobalThemeStateModel>, action: UpdateProjectThemes) {
    const globalThemeState = this.store.selectSnapshot(GlobalThemeState.getGlobalThemeState);
    const globalThemesArray = generateGlobalThemesCssStringsFromThemeConfig(
      globalThemeState.themes,
      globalThemeState.defaultThemeIndex ?? 0,
    );

    ctx.dispatch(new AddProgressBarWorkflowStack({ uniqueId: 'UpdateProjectThemes' }));
    this.apiService
      .updateProjectThemes(action.projectId, globalThemesArray)
      .pipe(
        finalize(() => ctx.dispatch(new RemoveProgressBarWorkflowStack({ uniqueId: 'UpdateProjectThemes' }))),
        tap((res) => {
          if (res) {
            this.alertHandlerService.showAlertFn(
              'success',
              'The theme has been successfully saved across the project.',
              'Save Successful',
            );
          }
        }),
        catchError((error) => {
          console.error(error, 'Error updating theme');
          this.alertHandlerService.showAlertFn(
            'error',
            'An error occurred while saving the theme. Please try again.',
            'Save Failed',
          );
          throw new Error(error);
        }),
      )
      .subscribe();
  }

  @Action(UpdateActiveThemeConfig)
  updateActiveThemeConfig(ctx: StateContext<GlobalThemeStateModel>, action: UpdateActiveThemeConfig) {
    const themesConfig = ctx.getState();
    themesConfig.themes[themesConfig.selectedThemeIndex] = action.themeConfig;
    ctx.patchState({
      ...ctx.getState(),
      themes: themesConfig.themes,
      selectedThemeIndex: themesConfig.selectedThemeIndex,
    });
  }

  @Action(UpdateGlobalThemeState)
  updateGlobalThemeState(ctx: StateContext<GlobalThemeStateModel>, action: UpdateGlobalThemeState) {
    ctx.patchState(action.globalThemeState);
    this.alertHandlerService.showAlertFn(
      'success',
      'The theme configuration has been successfully imported.',
      'Import Successful',
    );
  }

  @Action(AddNewGlobalThemeConfig)
  addNewGlobalThemeConfig(ctx: StateContext<GlobalThemeStateModel>, action: AddNewGlobalThemeConfig) {
    const themesConfig = ctx.getState();
    themesConfig.themes.push(action.themeConfig);
    ctx.patchState({
      ...ctx.getState(),
      themes: themesConfig.themes,
      selectedThemeIndex: action.setAsActiveTheme
        ? themesConfig.themes.length - 1
        : themesConfig.selectedThemeIndex,
    });
  }

  @Action(SetActiveTheme)
  setActiveTheme(ctx: StateContext<GlobalThemeStateModel>, action: SetActiveTheme) {
    const themesConfig = ctx.getState();
    const newActiveIndex = themesConfig.themes.findIndex(
      (v) => formatNameAsUrl(v.themeName) === formatNameAsUrl(action.themeName),
    );
    const defaultThemeIndex = themesConfig.themes.findIndex((v) => v.themeName.toLowerCase() === 'default');
    ctx.patchState({
      ...ctx.getState(),
      themes: themesConfig.themes,
      selectedThemeIndex: newActiveIndex,
      defaultThemeIndex: defaultThemeIndex,
    });
  }

  @Action(ResetThemesConfig)
  resetThemesConfig(ctx: StateContext<GlobalThemeStateModel>, action: ResetThemesConfig) {
    ctx.patchState({
      themes: [],
      selectedThemeIndex: 0,
    });
    this.fetchProjectThemes(ctx, { projectId: action.projectId });
  }
}
